import { CHART_GRID, TEXT_COLOR_SECONDARY } from "../../../../styles/theme_constants";

/**
 * BOF-76 — shared chart.js settings for the dashboard's panels.
 *
 * Follows the house data-viz rules: one y-axis per chart (never two scales), recessive grid and axes,
 * a legend whenever there is more than one series, a hover tooltip on every mark, and a 2px surface gap
 * between stacked segments so adjacent colours never touch. Animation is off: this is read, not watched.
 */
export const baseChartOptions = (opts: { stacked?: boolean; yTitle?: string; percent?: boolean } = {}) => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: false as const,
  interaction: { mode: "index" as const, intersect: false },
  plugins: {
    legend: {
      position: "top" as const,
      align: "start" as const,
      labels: { color: TEXT_COLOR_SECONDARY, boxWidth: 12, boxHeight: 12, font: { size: 12 } },
    },
    tooltip: {
      callbacks: opts.percent
        ? {
            label: (ctx: any) =>
              ctx.parsed.y == null ? `${ctx.dataset.label}: —` : `${ctx.dataset.label}: ${(ctx.parsed.y * 100).toFixed(1)}%`,
          }
        : {
            label: (ctx: any) =>
              ctx.parsed.y == null
                ? `${ctx.dataset.label}: —`
                : `${ctx.dataset.label}: ${Number(ctx.parsed.y).toLocaleString("en-GB", { maximumFractionDigits: 2 })}`,
          },
    },
  },
  scales: {
    x: {
      stacked: !!opts.stacked,
      grid: { display: false },
      ticks: { color: TEXT_COLOR_SECONDARY, font: { size: 11 }, maxRotation: 0, autoSkip: true },
    },
    y: {
      stacked: !!opts.stacked,
      beginAtZero: true,
      grid: { color: CHART_GRID },
      border: { display: false },
      title: opts.yTitle ? { display: true, text: opts.yTitle, color: TEXT_COLOR_SECONDARY, font: { size: 11 } } : undefined,
      ticks: {
        color: TEXT_COLOR_SECONDARY,
        font: { size: 11 },
        callback: opts.percent ? (v: number) => `${Math.round(v * 100)}%` : undefined,
      },
    },
  },
});

/** A bar series with the 2px surface gap; the top segment of a stack gets the rounded data-end. */
export const barSeries = (label: string, data: Array<number | null>, color: string, roundedTop: boolean) => ({
  label,
  data,
  backgroundColor: color,
  borderColor: "#ffffff",
  borderWidth: { top: 2, left: 0, right: 0, bottom: 0 },
  borderRadius: roundedTop ? { topLeft: 4, topRight: 4 } : 0,
  borderSkipped: false as const,
  maxBarThickness: 28,
});

/** A 2px line with markers large enough to hover. A null point leaves a gap rather than dropping to zero. */
export const lineSeries = (label: string, data: Array<number | null>, color: string, dashed = false) => ({
  label,
  data,
  borderColor: color,
  backgroundColor: color,
  borderWidth: 2,
  borderDash: dashed ? [5, 4] : [],
  pointRadius: 4,
  pointHoverRadius: 6,
  pointBorderColor: "#ffffff",
  pointBorderWidth: 2,
  spanGaps: false,
  tension: 0,
});

export const CHART_HEIGHT = { height: "220px" };
